import React, { useCallback, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDocumentStorage } from "../../hooks/useDocumentStorage";
import { DocumentStorageService } from "../../services/document-storage.service";
import {
  DOCUMENT_TYPE_LABELS,
  SavedDocument,
} from "../../models/document-storage.types";
import { getLogger } from "../../utils/logger";
import {
  DocumentList,
  SortDirection,
  SortKey,
  SortState,
} from "./DocumentList";
import { DocumentEmptyState } from "./DocumentEmptyState";
import { DocumentFilter, DocumentTypeFilter } from "./DocumentTypeFilter";
import { DocumentDeleteConfirm } from "./DocumentDeleteConfirm";

const logger = getLogger("DocumentLibrary");

const emptyCounts = (): Record<DocumentFilter, number> => ({
  all: 0,
  unit_of_work: 0,
  lesson_plan: 0,
  worksheet: 0,
  slideshow: 0,
});

/** Timestamp for ordering; unparseable dates sort oldest rather than poisoning
 *  the comparator with NaN. */
const timeOf = (iso: string): number => {
  const time = new Date(iso).getTime();
  return isNaN(time) ? 0 : time;
};

/** First click on a column picks the order teachers expect from it. */
const DEFAULT_DIRECTIONS: Record<SortKey, SortDirection> = {
  title: "asc",
  document_type: "asc",
  created_at: "desc",
};

const compareBy = (
  a: SavedDocument,
  b: SavedDocument,
  key: SortKey
): number => {
  switch (key) {
    case "title":
      return a.title.localeCompare(b.title, "en-AU", { sensitivity: "base" });
    case "document_type":
      // Ordered by the label the user actually sees, not the internal key.
      return DOCUMENT_TYPE_LABELS[a.document_type].localeCompare(
        DOCUMENT_TYPE_LABELS[b.document_type],
        "en-AU"
      );
    case "created_at":
    default:
      return timeOf(a.created_at) - timeOf(b.created_at);
  }
};

/**
 * Trigger a browser download for a presigned URL.
 *
 * The URL is served with `Content-Disposition: attachment; filename="<title
 * slug>"`, so the browser saves it under the document's own human-readable
 * name. No `download` attribute is set: it would be ignored for the
 * cross-origin S3 URL anyway, and the server-side name is the correct one.
 */
const triggerBrowserDownload = (url: string) => {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
};

/**
 * "My documents" library: type filter chips, title search, and one row per
 * saved document with download and delete.
 *
 * Filtering and search run over the list already cached by
 * DocumentStorageContext rather than re-querying the list endpoint on every
 * keystroke. The endpoint's `type`/`search` parameters exist for other callers;
 * at the volumes a single teacher generates, in-memory filtering is instant and
 * costs no extra requests.
 */
export const DocumentLibrary: React.FC = () => {
  const { documents, loading, error, refresh } = useDocumentStorage();
  const { getAccessTokenSilently } = useAuth0();

  const [filter, setFilter] = useState<DocumentFilter>("all");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState>({
    key: "created_at",
    direction: "desc",
  });
  const [actionError, setActionError] = useState<string | null>(null);

  // Per-action state.
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<SavedDocument | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Counts per filter (over the unfiltered list).
  const counts = useMemo(() => {
    const next = emptyCounts();
    next.all = documents.length;
    documents.forEach((doc) => {
      next[doc.document_type] += 1;
    });
    return next;
  }, [documents]);

  // Type filter + case-insensitive title search + the chosen column order.
  const visibleDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();
    return documents
      .filter((doc) => filter === "all" || doc.document_type === filter)
      .filter((doc) => !query || doc.title.toLowerCase().includes(query))
      .sort((a, b) => {
        const primary = compareBy(a, b, sort.key);
        if (primary !== 0) {
          return sort.direction === "asc" ? primary : -primary;
        }
        // Ties (same type, same title) keep newest-first so the order never
        // shuffles between renders.
        return timeOf(b.created_at) - timeOf(a.created_at);
      });
  }, [documents, filter, search, sort]);

  const handleSortChange = useCallback((key: SortKey) => {
    setSort((current) =>
      current.key === key
        ? { key, direction: current.direction === "asc" ? "desc" : "asc" }
        : { key, direction: DEFAULT_DIRECTIONS[key] }
    );
  }, []);

  const handleDownload = useCallback(
    async (doc: SavedDocument) => {
      setActionError(null);
      setDownloadingId(doc.document_id);
      try {
        const accessToken = await getAccessTokenSilently();
        const response = await DocumentStorageService.getDownloadUrl(
          doc.document_id,
          accessToken
        );
        if (response.error || !response.data) {
          throw new Error(
            response.error?.message || "Couldn’t prepare the download."
          );
        }
        triggerBrowserDownload(response.data.download_url);
      } catch (err) {
        logger.error("Download failed", err);
        setActionError(
          err instanceof Error ? err.message : "Couldn’t download the document."
        );
      } finally {
        setDownloadingId(null);
      }
    },
    [getAccessTokenSilently]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) {
      return;
    }
    setDeleting(true);
    setDeleteError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const response = await DocumentStorageService.deleteDocument(
        deleteTarget.document_id,
        accessToken
      );
      if (response.error || !response.data?.deleted) {
        throw new Error(response.error?.message || "Couldn’t delete.");
      }
      setDeleteTarget(null);
      refresh();
    } catch (err) {
      logger.error("Delete failed", err);
      setDeleteError(
        err instanceof Error ? err.message : "Couldn’t delete the document."
      );
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, getAccessTokenSilently, refresh]);

  const clearFilters = () => {
    setFilter("all");
    setSearch("");
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="document-library__status">
          <span className="document-library__spinner" aria-hidden="true" />
          Loading your documents…
        </div>
      );
    }

    if (error) {
      return (
        <div className="document-library__status">
          <div className="form-error-message">
            Couldn’t load your documents.
          </div>
          {/* The list is context-owned, so retrying in place is enough: the
              user never has to reload the page to recover from a blip. */}
          <button
            type="button"
            className="document-empty-state__cta"
            onClick={refresh}
          >
            Try again
          </button>
        </div>
      );
    }

    if (documents.length === 0) {
      return <DocumentEmptyState />;
    }

    if (visibleDocuments.length === 0) {
      return <DocumentEmptyState filtered onClearFilters={clearFilters} />;
    }

    return (
      <DocumentList
        documents={visibleDocuments}
        sort={sort}
        onSortChange={handleSortChange}
        onDownload={handleDownload}
        onDelete={(doc) => {
          setDeleteError(null);
          setDeleteTarget(doc);
        }}
        downloadingId={downloadingId}
      />
    );
  };

  return (
    <div className="page-container document-library">
      <div className="document-library__header">
        <h1 className="page-header__title document-library__heading">
          My documents
        </h1>
        <input
          type="search"
          className="document-library__search"
          placeholder="Search by title…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search documents by title"
        />
      </div>

      {documents.length > 0 && (
        <DocumentTypeFilter
          active={filter}
          counts={counts}
          onChange={setFilter}
        />
      )}

      {actionError && (
        <div className="form-error-message document-library__action-error">
          {actionError}
          <button
            type="button"
            className="document-library__dismiss"
            aria-label="Dismiss"
            onClick={() => setActionError(null)}
          >
            ×
          </button>
        </div>
      )}

      {renderContent()}

      {deleteTarget && (
        <DocumentDeleteConfirm
          document={deleteTarget}
          isDeleting={deleting}
          error={deleteError}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
